import { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { formaterDate, formaterDateHeure, formaterIdentite } from '@gsm/partage';
import { useRessource, messageErreur } from '../api/ressource';
import { api, ErreurApi } from '../api/client';
import { useAuth } from '../auth/contexte';
import { Bloc, Tableau } from '../composants/Tableau';
import { BandeauAlerte } from '../composants/BandeauAlerte';
import { Pastille } from '../composants/Pastille';
import { Nombre } from '../composants/Nombre';
import { Champ } from '../composants/Champ';
import { Modale } from '../composants/Modale';
import { Icone } from '../composants/Icone';
import { LienPiece } from '../composants/LienAction';
import { BlocSortieFonds } from '../composants/BlocSortieFonds';
import { LIBELLE_STATUT_MANDAT, TON_STATUT_MANDAT } from './Mandats';

interface Mandat {
  id: string;
  reference: string;
  statut: string;
  creePar: string;
  validePar: string | null;
  valideLe: string | null;
  dateActivation: string | null;
  dateSignature: string | null;
  profilDateDetermination: string;
  profilReferenceDossier: string | null;
  ibanCompteEspece: string | null;
  tauxFraisAnnuelPct: string;
  tamponTresoreriePct: string;
  motifSuspension: string | null;
  realignementRequisDepuis: string | null;
  client: { id: string; reference: string; nom: string; prenom: string; cin: string };
  agence: { code: string; libelle: string };
  profil: { id: string; code: string; libelle: string };
  documents: {
    id: string;
    typeDoc: string;
    nomFichier: string;
    deposeLe: string;
    retireLe: string | null;
    motifRetrait: string | null;
  }[];
  historiqueProfils: {
    id: string;
    dateDebut: string;
    dateFin: string | null;
    motif: string | null;
    profil: { libelle: string };
  }[];
}

type Manque = { champ?: string; message: string };

export function FicheMandat() {
  const { id } = useParams<{ id: string }>();
  const { profil: utilisateur, aRole } = useAuth();
  const mandat = useRessource<Mandat>(`/mandats/${id}`);
  const conditions = useRessource<Manque[]>(`/mandats/${id}/conditions-activation`);

  const [action, setAction] = useState<string | null>(null);
  const [erreurAction, setErreurAction] = useState<string | null>(null);
  const fichierRef = useRef<HTMLInputElement>(null);
  const [typeDoc, setTypeDoc] = useState('MANDAT_SIGNE');

  const [retrait, setRetrait] = useState<{ id: string; nom: string } | null>(null);
  const [motifRetrait, setMotifRetrait] = useState('');
  const [erreurRetrait, setErreurRetrait] = useState<string | null>(null);

  const m = mandat.donnees;
  const manques = conditions.donnees ?? [];
  const peutSaisir = aRole('MIDDLE_BO', 'RESP_GESTION', 'ADMIN');
  const peutRebalancer = aRole('GERANT', 'RESP_GESTION', 'ADMIN');

  async function rafraichir() {
    await Promise.all([mandat.recharger(), conditions.recharger()]);
  }

  async function agir(nom: string, appel: () => Promise<unknown>) {
    setErreurAction(null);
    setAction(nom);
    try {
      await appel();
      await rafraichir();
    } catch (e) {
      setErreurAction(e instanceof ErreurApi ? messageErreur(e) : 'Opération impossible.');
    } finally {
      setAction(null);
    }
  }

  async function deposer() {
    const fichier = fichierRef.current?.files?.[0];
    if (!fichier || !m) return;
    const formulaire = new FormData();
    formulaire.append('fichier', fichier);
    formulaire.append('clientId', m.client.id);
    formulaire.append('mandatId', m.id);
    formulaire.append('typeDoc', typeDoc);
    await agir('depot', () => api.deposer('/documents', formulaire));
    if (fichierRef.current) fichierRef.current.value = '';
  }

  function ouvrirRetrait(idPiece: string, nom: string) {
    setRetrait({ id: idPiece, nom });
    setMotifRetrait('');
    setErreurRetrait(null);
  }

  async function confirmerRetrait(evenement: { preventDefault: () => void }) {
    evenement.preventDefault();
    if (!retrait) return;
    setErreurRetrait(null);
    try {
      await api.post(`/documents/${retrait.id}/retirer`, { motif: motifRetrait });
      setRetrait(null);
      await rafraichir();
    } catch (e) {
      setErreurRetrait(e instanceof ErreurApi ? messageErreur(e) : 'Retrait impossible.');
    }
  }

  if (mandat.chargement) return <p className="surtitre">Chargement…</p>;
  if (mandat.erreur || !m)
    return (
      <BandeauAlerte nature="refus" titre="Mandat introuvable">
        {messageErreur(mandat.erreur) ?? 'Ce mandat n’existe pas.'}
      </BandeauAlerte>
    );

  const estAuteur = utilisateur?.id === m.creePar;
  // L'ADMIN est superutilisateur : il valide ses propres opérations (décision
  // du 04/08/2026). Le serveur lève déjà le contrôle pour lui — QuatreYeuxService
  // — ; l'interface doit suivre, sinon le bouton reste grisé sans recours.
  const validationBloquee = m.statut === 'EN_ATTENTE_VALIDATION' && estAuteur && !aRole('ADMIN');

  return (
    <>
      <p className="entete__fil">
        <Link to="/mandats">Mandats</Link> › {m.reference}
      </p>
      <div className="entete">
        <h1>{formaterIdentite(m.client.nom, m.client.prenom)}</h1>
        <span className="entete__ref">{m.reference}</span>
        <Pastille ton={TON_STATUT_MANDAT[m.statut] ?? 'neutre'}>
          {LIBELLE_STATUT_MANDAT[m.statut] ?? m.statut}
        </Pastille>
      </div>

      <section className="cles" aria-label="Chiffres clés">
        <div className="cle">
          <span className="surtitre">Profil de risque</span>
          <div className="cle__valeur">{m.profil.libelle}</div>
          <div className="appoint">déterminé le {formaterDate(m.profilDateDetermination)}</div>
        </div>
        <div className="cle">
          <span className="surtitre">Frais de gestion</span>
          <div className="cle__valeur">
            <Nombre valeur={m.tauxFraisAnnuelPct} nature="pourcentage" />
          </div>
        </div>
        <div className="cle">
          <span className="surtitre">Tampon de trésorerie</span>
          <div className="cle__valeur">
            <Nombre valeur={m.tamponTresoreriePct} nature="pourcentage" />
          </div>
        </div>
        <div className="cle">
          <span className="surtitre">Agence</span>
          <div className="cle__valeur">{m.agence.code}</div>
          <div className="appoint">{m.agence.libelle}</div>
        </div>
        <div className="cle">
          <span className="surtitre">Activation</span>
          <div className="cle__valeur">{formaterDate(m.dateActivation)}</div>
          {m.valideLe && <div className="appoint">validé le {formaterDateHeure(m.valideLe)}</div>}
        </div>
      </section>

      {erreurAction && (
        <BandeauAlerte nature="refus" titre="Opération refusée">
          {erreurAction}
        </BandeauAlerte>
      )}

      {m.motifSuspension && (
        <BandeauAlerte nature="gestion" titre="Mandat suspendu">
          {m.motifSuspension}
        </BandeauAlerte>
      )}

      {m.realignementRequisDepuis && (
        <BandeauAlerte nature="gestion" titre="Portefeuille non réaligné">
          Le profil a changé le {formaterDateHeure(m.realignementRequisDepuis)} : le portefeuille
          suit encore l’ancienne cible.{' '}
          {peutRebalancer
            ? 'Lancez le rebalancing depuis le menu « … » de la liste des mandats.'
            : 'Le gérant doit lancer le rebalancing.'}
        </BandeauAlerte>
      )}

      {manques.length > 0 && (
        <BandeauAlerte nature="gestion" titre="Conditions d’activation non réunies">
          {manques.map((x) => x.message).join(' ')}
        </BandeauAlerte>
      )}

      {validationBloquee && (
        <BandeauAlerte nature="gestion" titre="Validation impossible par vous">
          Vous avez saisi ce mandat : sa validation revient à une autre personne (principe des
          quatre yeux). C’est le seul contrôle interne applicatif de l’application.
        </BandeauAlerte>
      )}

      <Bloc titre="Profil de risque" meta="déterminé en agence, hors application">
        <Tableau
          colonnes={[
            { cle: 'profil', entete: 'Profil', rendu: (l) => l.profil.libelle },
            {
              cle: 'debut',
              entete: 'Depuis le',
              num: true,
              rendu: (l) => formaterDate(l.dateDebut),
            },
            { cle: 'fin', entete: "Jusqu'au", num: true, rendu: (l) => formaterDate(l.dateFin) },
            { cle: 'motif', entete: 'Motif', rendu: (l) => l.motif ?? '—' },
          ]}
          lignes={m.historiqueProfils}
          cle={(l) => l.id}
          vide={
            m.statut === 'BROUILLON' || m.statut === 'EN_ATTENTE_VALIDATION'
              ? 'Historique ouvert à la validation du mandat.'
              : 'Aucun historique enregistré pour ce mandat.'
          }
        />
        {/* Le profil courant est porté par le mandat lui-même : il s'affiche
            même quand l'historique est vide (mandat non encore validé, ou
            repris sans historique). */}
        <p className="consigne consigne--pied">
          Profil actuel : <strong>{m.profil.libelle}</strong>, déterminé le{' '}
          {formaterDate(m.profilDateDetermination)}
          {m.profilReferenceDossier ? ` · dossier ${m.profilReferenceDossier}` : ''}.
        </p>
      </Bloc>

      <Bloc titre="Pièces" meta={`${m.documents.length} document(s)`}>
        <Tableau
          colonnes={[
            { cle: 'type', entete: 'Type', rendu: (d) => d.typeDoc },
            {
              cle: 'nom',
              entete: 'Fichier',
              rendu: (d) => (
                <LienPiece href={`/api/v1/documents/${d.id}/contenu`}>{d.nomFichier}</LienPiece>
              ),
            },
            {
              cle: 'depot',
              entete: 'Déposé le',
              num: true,
              rendu: (d) => formaterDateHeure(d.deposeLe),
            },
            {
              cle: 'etat',
              entete: 'État',
              rendu: (d) =>
                d.retireLe ? (
                  <span className="etiquette-mention">
                    <Pastille ton="attente">Retirée</Pastille>
                    <span className="consigne">{d.motifRetrait ?? '—'}</span>
                  </span>
                ) : peutSaisir ? (
                  <button
                    type="button"
                    className="tableau__action"
                    onClick={() => ouvrirRetrait(d.id, d.nomFichier)}
                    aria-label={`Retirer ${d.nomFichier}`}
                  >
                    <Icone nom="fermer" taille={14} />
                  </button>
                ) : (
                  '—'
                ),
            },
          ]}
          lignes={m.documents}
          cle={(d) => d.id}
          vide="Aucune pièce."
        />

        {peutSaisir && m.statut === 'BROUILLON' && (
          <div className="barre-actions barre-actions--apres barre-actions--champs">
            <div className="champ">
              <label className="surtitre" htmlFor="typedoc">
                Type
              </label>
              <select
                id="typedoc"
                className="champ__select"
                value={typeDoc}
                onChange={(e) => setTypeDoc(e.target.value)}
              >
                <option value="MANDAT_SIGNE">Mandat signé</option>
                <option value="RIB">RIB</option>
              </select>
            </div>
            <input ref={fichierRef} type="file" accept=".pdf,.jpg,.jpeg,.png" />
            <button className="bouton" disabled={action === 'depot'} onClick={() => void deposer()}>
              {action === 'depot' ? 'Dépôt…' : 'Déposer'}
            </button>
          </div>
        )}
      </Bloc>

      <BlocSortieFonds mandatId={m.id} />

      <Modale
        ouverte={retrait !== null}
        onFermer={() => setRetrait(null)}
        surtitre={retrait?.nom}
        titre="Retirer cette pièce"
      >
        {erreurRetrait && (
          <BandeauAlerte nature="refus" titre="Retrait refusé">
            {erreurRetrait}
          </BandeauAlerte>
        )}
        <p className="consigne consigne--tete">
          La pièce n’est pas supprimée : le fichier et son empreinte restent conservés, et le
          retrait est journalisé. Elle cesse simplement de compter — un mandat signé retiré ne
          satisfait plus la RG-03.
        </p>
        <form onSubmit={(e) => void confirmerRetrait(e)} noValidate>
          <Champ
            label="Motif du retrait"
            value={motifRetrait}
            onChange={(e) => setMotifRetrait(e.target.value)}
            aide="Obligatoire — c’est lui qui rendra le geste relisible."
            required
          />
          <div className="modale__actions">
            <button
              type="button"
              className="bouton bouton--fantome"
              onClick={() => setRetrait(null)}
            >
              Annuler
            </button>
            <button className="bouton" type="submit" disabled={!motifRetrait.trim()}>
              Retirer la pièce
            </button>
          </div>
        </form>
      </Modale>
    </>
  );
}
